// 261015 P4c-1 — tab đầu của ②: "Tổng quan truyện". Mọi quyết định hiển thị ở `overviewView.ts` / `relationGraphLayout.ts` / `overviewEdit.ts` (thuần, có jest);
// file này chỉ vẽ + nối sự kiện. Không gọi AI ở đâu ngoài `startJob` (đã qua hộp hỏi chi phí). Không hiện tiền (Q6).
import { useCtaHolds } from '../WorkbenchContext';
import { overviewHoldsCta } from '../stageCtaView';
import React, { useMemo, useState } from 'react';
import { Alert, Dropdown, Input, Segmented, Select, Space, Spin, Tooltip, Typography } from 'antd';
import Button from '../WbButton';
import './overview.css';
import type { OutlinePatchOp, OutlineRecord, OverviewSection } from '../../../../types/adaptation';
import type { WorkbenchView } from '../../../../types/adaptationWorkbench';
import type { OverviewMode } from '../../../../services/api/adaptation';
import type { AssistantRequest } from '../assistant/assistantIntent';
import type { OverviewJobApi } from '../useOverviewJob';
import { friendlyJobNote } from '../jobErrorText';
import { Pill } from '../WbSlot';
import EvidenceDrawer from './EvidenceDrawer';
import OverviewEditModal from './OverviewEditModal';
import RelationGraph, { type RelationListRow } from './RelationGraph';
import { formSpec, type EditKind, type EditTarget } from './overviewEdit';
import { ASSISTANT_SCOPE_NOTE, assistRequest, type AssistTarget } from './overviewAssist';
import { buildNamePairs, type NameMode } from './overviewNames';
import { introLineStatus } from '../introLineView';
import {
  ACTION_LABEL, approveBlock, estimateLine, evidenceView, graphFromLegacy, graphFromOverview, legacyRelationRows, mergeDisabledReason, motivesView,
  GOLDEN_FINGER_HONESTY, MOTIVE_FACT_LABEL, MOTIVE_NOTE_HONESTY, MOTIVES_EDIT_NOTE,
  overviewBanners, overviewCounts, panelKind, plotViews, profileCards, progressView, propViews, PROFILE_FILTER_LABEL, relationRows, resultSummary, ruleViews, sourceScopeView,
  strategyView, synopsisClaims, unverifiedReferenced, claimView, type ApproveView, type Banner, type ClaimView, type ProfileFilter,
} from './overviewView';

const { Text } = Typography;
const NAME_KEY = 'talebox.adaptation.overview.nameMode';
const readNameMode = (): NameMode => { try { return window.localStorage.getItem(NAME_KEY) === 'film' ? 'film' : 'story'; } catch { return 'story'; } };
const saveNameMode = (m: NameMode): void => { try { window.localStorage.setItem(NAME_KEY, m); } catch { /* chế độ riêng tư / chặn lưu: bỏ qua */ } };
const pillTone = (t: 'ok' | 'bad' | 'warn' | 'neutral'): 'ok' | 'bad' | 'warn' | 'todo' => (t === 'neutral' ? 'todo' : t);
const scrollTo = (id: string): void => { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

interface Props {
  storyId: string;
  record: OutlineRecord;
  workbench: WorkbenchView | null;
  job: OverviewJobApi;
  /** Có việc khác đang chạy / đang ghi / ② bị khoá — tắt mọi nút ghi. */
  busy: boolean;
  approve: ApproveView;
  lint: { can: boolean; reason?: string };
  onApprove: () => void;
  onUnapprove: () => void;
  patchOverview: (ops: OutlinePatchOp[]) => Promise<boolean>;
  /** Hộp hỏi chi phí → gọi API tạo tổng quan (tự báo lỗi có mã + lối ra). Do `SkeletonStage` sở hữu để lối ra của mã lỗi cũng gọi lại được. */
  runAction: (mode: OverviewMode) => Promise<void>;
  requestAssistant: (r: AssistantRequest) => void;
  openStructure: () => void;
  /** Đang ở điểm dừng 1 của "Xử lý phần mới" (WAIT_SKELETON). */
  stop1: boolean;
  /** 261023 E3-P3c2b (H13): false ⇒ ẩn nút «Chốt tổng quan» (chuỗi truyện mới ở STOP 1: một lần bấm «Duyệt & chạy tiếp» ở dải chốt cả hai). Mặc định true. Nút «Mở để sửa» không bị ảnh hưởng. */
  showApprove?: boolean;
  /** R4/F1 — thẻ duyệt ở ② đang là nút đỏ ⇒ nút «Tạo/Chốt tổng quan» ở đây hạ trung tính. */
  cardHoldsCta?: boolean;
}

/** Một ý: câu + huy hiệu "đã tìm thấy trích"/"chưa xác minh" + xem trích (+ sửa / nhờ trợ lý khi được sửa). */
const ClaimLine: React.FC<{ c: ClaimView; canEdit: boolean; onEvidence: (id: string) => void; onEdit: (id: string) => void; onAsk: (t: AssistTarget) => void }> = ({ c, canEdit, onEvidence, onEdit, onAsk }) => (
  <div className="ov-claim">
    <span className="ov-claim-text">{c.text}</span>
    <span className="ov-claim-meta">
      <Pill tone={pillTone(c.badge.tone)} title={`Ý ${c.id} · ${c.kindText} · ${c.originText}`}>{c.badge.text}</Pill>
      <button type="button" className="ov-linkbtn" onClick={() => onEvidence(c.id)}>Xem trích</button>
      {canEdit && <button type="button" className="ov-linkbtn" onClick={() => onEdit(c.id)}>Sửa ý</button>}
      {canEdit && <button type="button" className="ov-linkbtn" onClick={() => onAsk({ type: 'claim', id: c.id, text: c.text })}>Nhờ trợ lý</button>}
    </span>
    {c.reason && <div className="ov-claim-why">{c.reason}</div>}
  </div>
);

const OverviewTab: React.FC<Props> = (p) => {
  const stageCta = overviewHoldsCta(useCtaHolds().holds('stage'), !!p.cardHoldsCta);
  const { record, workbench, job, busy, approve, lint } = p;
  const outline = record.outline;
  const ov = outline?.overview;
  const st = record.overviewState;
  const locked = record.status === 'APPROVED';
  const jobRunning = !!(job.running || job.jobId || job.starting);
  const canEdit = !!ov && !locked && !busy && !jobRunning;

  const [nameMode, setNameModeState] = useState<NameMode>(readNameMode);
  const setNameMode = (m: NameMode): void => { setNameModeState(m); saveNameMode(m); };
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ProfileFilter>('all');
  const [evidenceId, setEvidenceId] = useState<string | null>(null);
  const [edit, setEdit] = useState<{ kind: EditKind; target: EditTarget } | null>(null);

  const failedJob = (workbench?.jobs.failed ?? []).find((j) => j.kind === 'overview') ?? null;
  const kind = panelKind(!!ov, jobRunning ? { jobId: job.jobId ?? job.running?.jobId ?? '' } : null, failedJob);

  const ask = (t: AssistTarget): void => p.requestAssistant(assistRequest(t));
  const openEdit = (k: EditKind, target: EditTarget = {}): void => { if (canEdit) setEdit({ kind: k, target }); };
  // Chụp `ov` LÚC MỞ hộp (chỉ tính lại khi `edit` đổi): mọi `refreshAll()` (lưu bị từ chối, job nền xong…) tạo `ov` mới, mà hộp reset ô nhập mỗi khi `spec` đổi — không được xoá chữ người đang gõ.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const spec = useMemo(() => (edit && ov ? formSpec(edit.kind, edit.target, ov, outline) : null), [edit]);

  const runAction = p.runAction;

  // ── trạng thái không có tổng quan ────────────────────────────────────────────────────────────
  if (!outline) return null;

  if (kind === 'running' && !ov) {
    const pv = progressView(job.running?.progress);
    return (
      <div className="ov-center" role="status" aria-live="polite" id="section-overview">
        <Spin />
        <h2>Đang tạo tổng quan</h2>
        <div className="ov-steps">{pv.steps.map((s) => <span key={s.key} className={s.state === 'run' ? 'ov-step-run' : s.state === 'todo' ? 'ov-step-todo' : ''}>{s.state === 'done' ? '✓' : s.state === 'run' ? '⏳' : '○'} {s.label}</span>)}</div>
        <Text>{pv.line}</Text>
        <Text type="secondary" style={{ fontSize: 13 }}>Chạy ở nền: bạn có thể rời màn hình và quay lại; kết quả sẽ hiện ở đây khi xong. Đọc từng quyển rồi mới gộp — truyện dài có thể mất vài phút.</Text>
        <Button disabled>Đang tạo…</Button>
      </div>
    );
  }

  if (!ov) {
    const required = !!(st?.required || outline.params?.overviewRequired);
    const legacy = graphFromLegacy(outline, nameMode);
    const failMsg = failedJob ? friendlyJobNote(failedJob.note) || 'Không rõ nguyên nhân' : job.failure;
    return (
      <div className="ov" id="section-overview">
        <div className="ov-center">
          <h2>{failedJob || job.failure ? 'Chưa tạo được tổng quan' : 'Chưa có tổng quan truyện'}</h2>
          {(failedJob || job.failure) && <Alert type="error" showIcon style={{ textAlign: 'left' }} message={`Lần tạo gần nhất bị lỗi: ${failMsg}`} description="Khung xương hiện tại vẫn được giữ nguyên." />}
          <Text>{required
            ? 'Khung xương đã có. Truyện này cần một bản đọc về diễn biến, nhân vật, quan hệ và chi tiết quan trọng — có trích nguồn — rồi chốt ở đây trước khi chia tập.'
            : 'Khung xương đã có. Bạn có thể tạo một bản đọc về diễn biến, nhân vật, quan hệ và chi tiết quan trọng, có trích nguồn, từ các chương đã nạp.'}</Text>
          <Text type="secondary" style={{ fontSize: 13 }}>Truyện cũ tiếp tục theo cách đang dùng. Hệ thống chỉ tạo tổng quan khi bạn bấm.</Text>
          <Text strong>{estimateLine('full', { hasOverview: false, estimate: st?.estimate })}</Text>
          {record.status === 'APPROVED' && <Text type="warning" style={{ fontSize: 13 }}>Khung xương đang đã duyệt: tạo tổng quan sẽ đưa nó về nháp, cần chốt lại.</Text>}
          <Space wrap>
            <Button type="primary" cta={stageCta} disabled={busy || jobRunning} onClick={() => runAction('full')}>{failedJob || job.failure ? 'Thử lại (tốn AI)' : 'Tạo tổng quan (tốn AI)'}</Button>
            <Button type="link" onClick={p.openStructure}>Xem cấu trúc hiện có</Button>
          </Space>
        </div>
        {legacy && (
          <div className="ov-section">
            <div className="ov-section-h"><h2>Sơ đồ quan hệ từ bước bản địa hoá</h2>
              <Segmented size="small" value={nameMode} onChange={(v) => setNameMode(v as NameMode)} options={[{ value: 'story', label: 'Tên truyện' }, { value: 'film', label: 'Tên phim' }]} />
            </div>
            <RelationGraph input={legacy} rows={legacyRelationRows(outline, nameMode)} unverifiedNote="Chưa xác minh trích: các quan hệ này do bước bản địa hoá rút ra, chưa gắn với trích nguyên tác. Tạo tổng quan để có bản có trích." />
          </div>
        )}
      </div>
    );
  }

  // ── có tổng quan ─────────────────────────────────────────────────────────────────────────────
  const scope = sourceScopeView(ov, st?.missingVolumes);
  const banners = overviewBanners(record, failedJob ? { jobId: failedJob.jobId, note: friendlyJobNote(failedJob.note), endedAt: failedJob.endedAt } : null);
  const bad = unverifiedReferenced(ov).map((c) => claimView(c));
  const synopsis = synopsisClaims(ov, outline, nameMode);
  const plots = plotViews(ov, outline, nameMode);
  const strat = strategyView(ov, outline, nameMode);
  const motives = motivesView(ov, outline, nameMode);
  const profiles = profileCards(ov, outline, { mode: nameMode, query, filter });
  const rels = relationRows(ov, outline, nameMode);
  const graph = graphFromOverview(ov, outline, nameMode);
  const props = propViews(ov, outline, nameMode);
  const rules = ruleViews(ov, outline, nameMode);
  const relRows: RelationListRow[] = rels.map((r) => ({ id: r.id, sentence: r.sentence, factionLabel: r.factionLabel, type: r.type, aId: r.aId, bId: r.bId, claims: r.claims, verified: r.verified }));
  const blockReason = approveBlock(lint, approve);
  const pv = progressView(job.running?.progress);
  const summary = resultSummary(job.result);
  const mergeBlock = mergeDisabledReason(st, true);
  const evidence = evidenceId ? evidenceView(ov, evidenceId, nameMode === 'film' ? buildNamePairs(outline.characters) : []) : null;   // đổi tên chỉ ở văn xuôi của ý; TRÍCH giữ nguyên chữ gốc

  const bannerAction = (b: Banner): React.ReactNode => {
    if (!b.action) return null;
    const label = b.action === 'regenerate' && b.key === 'stale-content' && p.stop1 ? 'Cập nhật tổng quan cho phần mới' : b.actionLabel;
    const disabled = busy || jobRunning || !!b.actionDisabledReason;
    const btn = (
      <Button size="small" disabled={b.action === 'unverified' ? false : disabled}
        onClick={() => { if (b.action === 'regenerate') runAction('full'); else if (b.action === 'merge') runAction('merge'); else scrollTo('ov-unverified'); }}>{label}</Button>
    );
    return b.actionDisabledReason ? <Tooltip title={b.actionDisabledReason}><span>{btn}</span></Tooltip> : btn;
  };

  const claimsOf = (list: ClaimView[]): React.ReactNode => list.map((c) => (
    <ClaimLine key={c.id} c={c} canEdit={canEdit} onEvidence={setEvidenceId} onEdit={(id) => openEdit('claim', { id })} onAsk={ask} />
  ));
  const addClaim = (section: OverviewSection, ownerId = ''): React.ReactNode => (
    canEdit ? <button type="button" className="ov-linkbtn" onClick={() => openEdit('claim-add', { section, ownerId })}>+ Thêm ý</button> : null
  );
  const editBtn = (label: string, k: EditKind, target: EditTarget = {}): React.ReactNode => (canEdit ? <Button size="small" onClick={() => openEdit(k, target)}>{label}</Button> : null);

  const menuItems = [
    { key: 'full', label: ACTION_LABEL.full(true), onClick: () => runAction('full') },
    { key: 'force', label: ACTION_LABEL.force(true), onClick: () => runAction('force') },
    { key: 'merge', label: mergeBlock ? `${ACTION_LABEL.merge(true)} (không dùng được: ${mergeBlock})` : ACTION_LABEL.merge(true), disabled: !!mergeBlock, onClick: () => runAction('merge') },
  ];

  return (
    <div className="ov" id="section-overview">
      <div className="ov-head">
        <div className="ov-head-main">
          <div className="ov-title">
            <h2>Tổng quan truyện</h2>
            {locked ? <Pill tone="ok">Đã chốt</Pill> : bad.length > 0 ? <Pill tone="bad">Còn trích chưa xác minh</Pill> : <Pill tone="warn">Bản nháp</Pill>}
          </div>
          <div className="ov-sub">{scope.chaptersText} · {overviewCounts(ov)}</div>
        </div>
        <div className="ov-actions">
          <Segmented size="small" value={nameMode} onChange={(v) => setNameMode(v as NameMode)} options={[{ value: 'story', label: 'Tên truyện' }, { value: 'film', label: 'Tên phim' }]} />
          {/* 261015 P4c-1: 4 nút AI (Tạo lại / Đọc lại toàn bộ / Gộp lại) luôn có — kể cả khi ② đã chốt; khi đó hộp hỏi nói thẳng "sẽ đưa khung xương về nháp, cần chốt lại". */}
          <Dropdown menu={{ items: menuItems }} disabled={busy || jobRunning} trigger={['click']}><Button disabled={busy || jobRunning}>Tạo lại… (tốn AI)</Button></Dropdown>
          {locked
            ? <Button disabled={busy || jobRunning} onClick={p.onUnapprove}>{approve.unapproveLabel}</Button>
            : p.showApprove === false ? null : <Tooltip title={blockReason ?? undefined}><span><Button type="primary" cta={stageCta} disabled={busy || jobRunning || !!blockReason} onClick={p.onApprove}>{approve.approveLabel}</Button></span></Tooltip>}
        </div>
      </div>

      {jobRunning && (
        <Alert type="info" showIcon role="status" message={`Đang tạo tổng quan: ${pv.line}`} description="Bạn có thể xem nhưng chưa sửa được trong lúc này. Kết quả mới sẽ thay bản hiện tại thành một bản nháp mới." />
      )}
      {summary && !jobRunning && (
        <Alert type={summary.tone} showIcon closable onClose={job.dismiss} message={summary.title} description={<div>{summary.lines.map((l, i) => <div key={i}>{l}</div>)}</div>} />
      )}
      {job.failure && !failedJob && !jobRunning && <Alert type="error" showIcon closable onClose={job.dismiss} message={`Lần tạo vừa rồi bị lỗi: ${job.failure}`} description="Tổng quan hiện tại giữ nguyên." />}

      {banners.map((b) => (
        <div key={b.key} className={`ov-banner ov-banner-${b.tone}`} role={b.tone === 'error' ? 'alert' : 'status'}>
          <div className="ov-banner-t"><b>{b.title}</b>{b.text && <p>{b.text}</p>}</div>
          {bannerAction(b)}
        </div>
      ))}

      <div className="ov-scope">
        <div>{scope.completenessText || (scope.completeness === 'COMPLETE' ? 'Đã đọc đủ nguyên tác đã nạp.' : 'Nguồn đã nạp tới đây; chưa xác nhận đã hết truyện.')}</div>
        {scope.completeness !== 'COMPLETE' && <div>Phần ngoặt và kết chỉ ghi tới những gì nguồn đã nói — không dự đoán phần chưa nạp.</div>}
        <div className="ov-vols">{scope.volumes.map((v) => <Pill key={v.index} tone={v.status === 'DONE' ? (v.claimCount === 0 ? 'warn' : 'ok') : 'bad'}>{v.text}</Pill>)}</div>
        {scope.emptyVolumes.length > 0 && <div>Quyển {scope.emptyVolumes.join(', ')} đọc xong nhưng AI không bóc được ý nào — không phải quyển thiếu, nhưng nên xem lại nguyên tác.</div>}
      </div>

      <div className="ov-note">{ASSISTANT_SCOPE_NOTE}</div>

      {bad.length > 0 && (
        <div id="ov-unverified" className="ov-section" style={{ scrollMarginTop: 12 }}>
          <h3>Ý chưa tìm thấy trích ({bad.length}) — cần sửa hoặc xoá trước khi chốt</h3>
          {claimsOf(bad)}
        </div>
      )}

      <nav className="ov-toc" aria-label="Mục lục tổng quan">
        {([['ov-synopsis', 'Diễn biến'], ['ov-strategy', 'Mở tập 1'], ['ov-motives', 'Động cơ hấp dẫn'], ['ov-profiles', `Hồ sơ (${ov.profiles.length})`], ['ov-graph', 'Sơ đồ quan hệ'], ['ov-props', 'Đạo cụ & luật']] as const).map(([id, label]) => (
          <button key={id} type="button" className="ov-linkbtn" onClick={() => scrollTo(id)}>{label}</button>
        ))}
        <button type="button" className="ov-linkbtn" onClick={p.openStructure}>Cấu trúc chuyển thể →</button>
      </nav>

      <section id="ov-synopsis" className="ov-section">
        <div className="ov-section-h"><h2>Tóm tắt truyện</h2><Space size={8}>{addClaim('synopsis')}</Space></div>
        {synopsis.length ? claimsOf(synopsis) : <div className="ov-empty">Chưa có ý nào trong phần tóm tắt.</div>}
        <div className="ov-four" style={{ marginTop: 14 }}>
          {plots.map((pt) => (
            <div key={pt.key} className={`ov-card${pt.status === 'KNOWN' ? '' : ' ov-card-empty'}`}>
              <div className="ov-card-h"><h3>{pt.label}</h3><Space size={6}>{pt.status !== 'KNOWN' && <Pill tone="warn">{pt.statusText}</Pill>}{editBtn('Sửa', 'plot', { part: pt.key })}</Space></div>
              {pt.note && <p style={{ margin: '0 0 8px' }}>{pt.note}</p>}
              {pt.claims.length ? claimsOf(pt.claims) : pt.status === 'KNOWN' ? <div className="ov-empty">Chưa có ý nền.</div> : null}
              {pt.status !== 'KNOWN' && !pt.note && <div className="ov-empty">Không dự đoán phần nguồn chưa nói.</div>}
              {addClaim(`plot.${pt.key}` as OverviewSection)}
            </div>
          ))}
        </div>
      </section>

      <section id="ov-strategy" className="ov-section">
        <div className="ov-section-h"><h2>Chiến lược mở đầu tập 1</h2><Space size={8}><Pill tone="ind">Đề xuất chuyển thể</Pill>{editBtn('Sửa chiến lược', 'strategy')}{editBtn('Thêm giới thiệu', 'introduction')}</Space></div>
        {strat.approach ? <p style={{ marginTop: 0 }}>{strat.approach}</p> : <div className="ov-empty">Chưa có chiến lược mở đầu.</div>}
        {strat.introductions.map((i) => (
          <div key={i.profileId} className="ov-card" style={{ marginBottom: 8 }}>
            <div className="ov-card-h"><h3>Giới thiệu: {i.who}</h3>{editBtn('Sửa', 'introduction', { profileId: i.profileId })}</div>
            {i.how && <p style={{ margin: '0 0 6px' }}>{i.how} <Pill tone="ind">đề xuất</Pill></p>}
            {claimsOf(i.basis)}
            {i.introLine && <div className="ov-vo"><div className="ov-vo-t">V.O.: “{i.introLine.text}”</div><div className="ov-note">Chỉ dùng khi nhân vật xuất hiện lần đầu{i.introLine.availableFromChapter ? ` (nguồn từ chương ${i.introLine.availableFromChapter})` : ''}.</div></div>}
          </div>
        ))}
        <div className="ov-section-h" style={{ marginTop: 14 }}><h3>Câu V.O. giới thiệu ({strat.introLines.length})</h3>{editBtn('Thêm câu V.O.', 'introLine')}</div>
        {strat.introLines.length === 0 && <div className="ov-empty">Chưa có câu V.O. giới thiệu nào.</div>}
        {strat.introLines.map((l) => (
          <div key={l.id} className="ov-vo">
            <div className="ov-card-h"><span className="ov-note">{l.purposeText}: <b>{l.subject}</b>{l.availableFromChapter ? ` · dùng được từ chương ${l.availableFromChapter}` : ''}</span>
              <Space size={8}>{canEdit && <button type="button" className="ov-linkbtn" onClick={() => openEdit('introLine', { id: l.id })}>Sửa</button>}{canEdit && <button type="button" className="ov-linkbtn" onClick={() => ask({ type: 'introLine', id: l.id, text: l.text })}>Nhờ trợ lý</button>}</Space></div>
            <div className="ov-vo-t">“{l.text}”</div>
            {(() => {
              const us = introLineStatus(record.introLineUse?.[l.id], l.text);   // BE tính (D9) — FE chỉ in lại
              return us && (
                <div className={us.tone === 'warn' ? 'ov-note ov-note-warn' : 'ov-note'} style={{ marginTop: 6 }}>
                  {us.headline}
                  {us.filmText && <div>Chữ khi dùng (đã thay tên): <i>“{us.filmText}”</i></div>}
                </div>
              );
            })()}
            {l.basis.map((c) => <ClaimLine key={c.id} c={c} canEdit={false} onEvidence={setEvidenceId} onEdit={() => undefined} onAsk={ask} />)}
          </div>
        ))}
      </section>

      <section id="ov-motives" className="ov-section">
        <div className="ov-section-h"><h2>Động cơ hấp dẫn</h2></div>
        <div className="ov-note">{MOTIVES_EDIT_NOTE}</div>
        {!motives.exists && <div className="ov-empty">Tổng quan này chưa có phần Động cơ hấp dẫn.</div>}
        <div className="ov-motive-grid">
          <div className="ov-card">
            <div className="ov-card-h"><h3>Bàn tay vàng <Pill tone="ok">{MOTIVE_FACT_LABEL}</Pill></h3><Space size={6}>{editBtn('Sửa lựa chọn', 'golden-finger')}</Space></div>
            {motives.goldenFinger.claims.length ? claimsOf(motives.goldenFinger.claims) : <div className="ov-empty">Chưa có ý nào. Truyện không có lợi thế đặc biệt thì để trống.</div>}
            {motives.goldenFinger.missing > 0 && <div className="ov-claim-why">{motives.goldenFinger.missing} ý trong danh sách không còn trong tổng quan — sửa lựa chọn để gỡ.</div>}
            {motives.goldenFinger.canAdd && addClaim('goldenFinger')}
            <div className="ov-note">{GOLDEN_FINGER_HONESTY}</div>
          </div>
          {motives.groups.map((g) => (
            <div key={g.group} className="ov-card">
              <div className="ov-card-h"><h3>{g.label} <Pill tone="ind">{g.badge}</Pill></h3></div>
              {g.notes.length === 0 && <div className="ov-empty">Chưa có ghi chú nào.</div>}
              {g.notes.map((n) => (
                <div key={n.id} className="ov-motive-note">
                  <p style={{ margin: '0 0 4px' }}>{n.text}</p>
                  <div className="ov-note">Dựa trên {n.basis.length} ý nền{n.unverified > 0 ? <> · <b>{n.unverified} ý nền chưa xác minh</b></> : null} · {n.availableText}</div>
                  {n.basis.map((c) => <ClaimLine key={c.id} c={c} canEdit={false} onEvidence={setEvidenceId} onEdit={() => undefined} onAsk={ask} />)}
                  {n.missingBasis > 0 && <div className="ov-claim-why">{n.missingBasis} ý nền không còn trong tổng quan.</div>}
                  <Space size={8}>{canEdit && <button type="button" className="ov-linkbtn" onClick={() => openEdit('motive-note', { id: n.id, group: g.group })}>Sửa ghi chú</button>}</Space>
                </div>
              ))}
              {g.canAdd && canEdit && <button type="button" className="ov-linkbtn" onClick={() => openEdit('motive-note', { group: g.group })}>+ Thêm ghi chú</button>}
              {!g.canAdd && <div className="ov-note">Đã đủ số ghi chú tối đa của nhóm này.</div>}
            </div>
          ))}
        </div>
        <div className="ov-note" style={{ marginTop: 6 }}>{MOTIVE_NOTE_HONESTY}</div>
      </section>

      <section id="ov-profiles" className="ov-section">
        <div className="ov-section-h"><h2>Hồ sơ nhân vật <span className="ov-note">{ov.profiles.length}</span></h2>{editBtn('Thêm hồ sơ', 'profile')}</div>
        <div className="ov-profile-tools">
          <Input allowClear style={{ width: 240 }} placeholder="Tìm tên, vai hoặc tên trong phim…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Tìm nhân vật" />
          <Select style={{ width: 230 }} value={filter} onChange={(v) => setFilter(v)} aria-label="Lọc hồ sơ" options={(Object.keys(PROFILE_FILTER_LABEL) as ProfileFilter[]).map((k) => ({ value: k, label: PROFILE_FILTER_LABEL[k] }))} />
          <Text type="secondary" style={{ fontSize: 13 }}>Đang hiện {profiles.shown} / {profiles.total} hồ sơ</Text>
        </div>
        <div className="ov-note">Ngoại hình trong truyện chỉ giúp đọc hiểu; nhận dạng hình ảnh và hồ sơ Bank vẫn quản lý riêng. “Đã cắt / gộp” là quyết định của khung xương, không phải của tổng quan.</div>
        {profiles.groups.map((g) => (
          <div key={g.key}>
            <div className="ov-profile-group">{g.label} ({g.cards.length})</div>
            <div className="ov-profile-list">
              {g.cards.map((c) => (
                <details key={c.id} className="ov-profile" open={g.cards.length <= 2 && profiles.shown <= 4}>
                  <summary>
                    {c.title}{c.sub ? <span className="ov-note"> ({c.sub})</span> : null}
                    <span className="ov-profile-meta">
                      <Pill tone={pillTone(c.dispositionTone)} title={c.dispositionText}>{c.disposition === 'KEPT' ? 'giữ' : c.disposition === 'CUT' ? 'đã cắt' : c.disposition === 'MERGED' ? 'đã gộp' : 'chưa ánh xạ'}</Pill>
                      {c.unverified > 0 && <Pill tone="bad">{c.unverified} ý chưa xác minh</Pill>}
                    </span>
                  </summary>
                  <div className="ov-note" style={{ marginTop: 4 }}>{c.dispositionText}{c.firstChapter ? ` · xuất hiện lần đầu ở chương ${c.firstChapter}` : ''}{c.relationCount ? ` · ${c.relationCount} quan hệ` : ''}</div>
                  <dl>
                    {c.fields.filter((f) => f.claims.length > 0).map((f) => (<React.Fragment key={f.key}><dt>{f.label}</dt><dd>{claimsOf(f.claims)}</dd></React.Fragment>))}
                    {c.appearanceNote && (<><dt>Ghi chú ngoại hình</dt><dd>{c.appearanceNote}</dd></>)}
                    {c.fields.every((f) => f.claims.length === 0) && !c.appearanceNote && <dd className="ov-empty">Nguồn chưa nói gì thêm về nhân vật này — không suy đoán.</dd>}
                  </dl>
                  <Space size={8} wrap>
                    {canEdit && <Button size="small" onClick={() => openEdit('profile', { id: c.id })}>Sửa hồ sơ</Button>}
                    {canEdit && <Button size="small" onClick={() => ask({ type: 'profile', id: c.id, label: c.title })}>Nhờ trợ lý</Button>}
                    {canEdit && <button type="button" className="ov-linkbtn" onClick={() => openEdit('claim-add', { section: 'profile.personality', ownerId: c.id })}>+ Thêm ý (tính cách)</button>}
                    {canEdit && <button type="button" className="ov-linkbtn" onClick={() => openEdit('claim-add', { section: 'profile.behavior', ownerId: c.id })}>+ Thêm ý (hành vi)</button>}
                    <button type="button" className="ov-linkbtn" onClick={() => scrollTo('ov-graph')}>Xem quan hệ</button>
                  </Space>
                </details>
              ))}
            </div>
          </div>
        ))}
        {profiles.shown === 0 && <div className="ov-empty">Không có hồ sơ nào khớp.</div>}
      </section>

      <section id="ov-graph" className="ov-section">
        <div className="ov-section-h"><h2>Sơ đồ quan hệ trong truyện</h2></div>
        <RelationGraph
          input={graph} rows={relRows} canEdit={canEdit} onEvidence={setEvidenceId} onEditRelation={(id) => openEdit('relationship', { id })} onAddRelation={() => openEdit('relationship', {})}
          onAsk={(id, sentence) => ask({ type: 'relationship', id, label: sentence })}
        />
        <div className="ov-note" style={{ marginTop: 6 }}>Nhân vật đã cắt trong bản phim vẫn có thể xuất hiện ở tổng quan nguyên tác; mỗi hồ sơ có nhãn ánh xạ riêng. Phe: {ov.factions.length ? ov.factions.map((f) => f.label).join(' · ') : 'chưa có'}.</div>
        {canEdit && <Space size={8} style={{ marginTop: 6 }}>{editBtn('Thêm phe', 'faction')}{ov.factions.map((f) => <button key={f.id} type="button" className="ov-linkbtn" onClick={() => openEdit('faction', { id: f.id })}>Sửa phe “{f.label}”</button>)}</Space>}
      </section>

      <section id="ov-props" className="ov-section">
        <div className="ov-section-h"><h2>Đạo cụ then chốt &amp; luật thế giới</h2><Space size={8}>{editBtn('Thêm đạo cụ', 'prop')}{editBtn('Thêm luật', 'rule')}</Space></div>
        <div className="ov-prop-grid">
          {props.map((x) => (
            <div key={x.id} className="ov-card">
              <div className="ov-card-h"><span><Pill tone="todo">Đạo cụ</Pill> <b>{x.name}</b></span>{editBtn('Sửa', 'prop', { id: x.id })}</div>
              {x.aliases.length > 0 && <div className="ov-note">Còn gọi: {x.aliases.join(', ')}</div>}
              {claimsOf(x.claims)}
              {x.importance && <p style={{ margin: '6px 0 0' }}>{x.importance} <Pill tone="ind">đề xuất chuyển thể</Pill></p>}
              {x.chapters.length > 0 && <div className="ov-note">Xuất hiện ở chương {x.chapters.join(', ')}.</div>}
              {addClaim('prop', x.id)}
            </div>
          ))}
          {rules.map((x) => (
            <div key={x.id} className="ov-card">
              <div className="ov-card-h"><span><Pill tone="ind">Luật thế giới</Pill> <b>{x.title}</b></span>{editBtn('Sửa', 'rule', { id: x.id })}</div>
              {claimsOf(x.claims)}
              {addClaim('worldRule', x.id)}
            </div>
          ))}
        </div>
        {props.length === 0 && rules.length === 0 && <div className="ov-empty">Nguồn đã nạp chưa có đạo cụ then chốt hay luật thế giới nào được ghi nhận.</div>}
        {props.length > 0 && rules.length === 0 && <div className="ov-note" style={{ marginTop: 8 }}>Chưa có luật thế giới nào được ghi nhận từ nguồn đã đọc.</div>}
      </section>

      <section className="ov-section">
        <h2>Cấu trúc chuyển thể hiện có</h2>
        <div className="ov-note">Sửa tổng quan ở trên không tự sửa khung xương: nhân vật, cao trào, giữ / cắt / gộp và các tập vẫn như cũ. Phần lệch chỉ được báo ở đầu trang.</div>
        <Button size="small" style={{ marginTop: 6 }} onClick={p.openStructure}>Mở “Cấu trúc chuyển thể”</Button>
      </section>

      <EvidenceDrawer
        open={!!evidenceId} evidence={evidence} onClose={() => setEvidenceId(null)} canEdit={canEdit}
        onEdit={(id) => { setEvidenceId(null); openEdit('claim', { id }); }} onAsk={(id, text) => { setEvidenceId(null); ask({ type: 'claim', id, text }); }}
      />
      <OverviewEditModal spec={spec} target={edit?.target ?? {}} ov={ov} outline={outline} onCancel={() => setEdit(null)} onSubmit={p.patchOverview} />
    </div>
  );
};

export default OverviewTab;
